import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { nestApi } from "@/lib/api";

const AuthContext = createContext(null);

const decodeJwtPayload = (token) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await nestApi.get("/rahe-portal/me");
      setUser(data);
    } catch {
      localStorage.removeItem("humrahe_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("humrahe_token");
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (token, userData) => {
    if (!token) throw new Error("Login response did not include an auth token");

    localStorage.setItem("humrahe_token", token);
    if (userData && typeof userData === "object") {
      setUser(userData);
      setLoading(false);
      return userData;
    }

    try {
      const { data } = await nestApi.get("/rahe-portal/me");
      setUser(data);
      setLoading(false);
      return data;
    } catch {
      const payload = decodeJwtPayload(token);
      const fallbackUser = payload
        ? { id: payload.sub, email: payload.email }
        : null;
      setUser(fallbackUser);
      setLoading(false);
      return fallbackUser;
    }
  };

  const logout = () => {
    localStorage.removeItem("humrahe_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
